import { CvValue, EditorialProject } from '@/models'
import ApiClient from '@/services/api-client'
import {
  CV_VALUE_COURSE_LEVEL,
  CV_VALUE_EDUCATION,
  CV_VALUE_MATERIAL_TYPE,
  CV_VALUE_PRODUCT_COLLECTION,
  CV_VALUE_PRODUCT_SERIES,
  CV_VALUE_PRODUCT_WORK_TYPE,
  CV_VALUE_SHORT_CODE_INDEF,
  CV_VALUE_SUBJECT
} from '@/utils/constants'

/**
 * Product form fields shown depending on the material types of the production project.
 *
 * - Fixed fields are always shown (the organizational levels depend on the project instead).
 * - A field listed in the metadata of some material type (comma-separated field names) is only
 *   shown when the production project has one of those material types. It is then required,
 *   unless its name ends with '?' in every one of them (e.g. 'collection,series?').
 * - Any other field is always shown and optional.
 */
export const PRODUCT_FIXED_REQUIRED_FIELDS = [
  'code', 'name', 'type', 'editorialProject', 'country', 'campaign', 'productionCampaign'
]
export const PRODUCT_FIXED_OPTIONAL_FIELDS = ['language', 'region', 'wwIssueId', 'wwIssue', 'wwBrandId', 'wwBrand']
// Switches always have a value, so they never block the form
export const PRODUCT_BOOLEAN_FIELDS = ['assetsActive', 'sendBtovenFromStructureLdx', 'isModel']

const OPTIONAL_FIELD_SUFFIX = '?'

export type ProductFieldKind = 'text' | 'url' | 'date' | 'switch' | 'cv' | 'team'

/** A product field that material types can list, and how the form and the detail handle it */
export interface ProductFieldDefinition {
  field: string;
  kind: ProductFieldKind;
  // Name in the i18n keys upsert_product_modal_component.<i18n>_label and _placeholder
  i18n: string;
  // Controlled vocabulary of a 'cv' field
  cvType?: string;
  // Stored with the CV short code ('edct-pri' is saved as 'pri')
  shortCode?: boolean;
  // Saved in the product itself instead of its metadata
  onProduct?: boolean;
  // Metadata key that also keeps the name of the chosen CV value
  nameField?: string;
}

/** Fields the material types can list. The ones no type lists go with the general fields */
export const PRODUCT_TYPE_FIELDS: ProductFieldDefinition[] = [
  { field: 'parentFolderEcUrl', kind: 'url', i18n: 'parent_folder_ec_url' },
  {
    field: 'subject', kind: 'cv', i18n: 'subject_label', cvType: CV_VALUE_SUBJECT, shortCode: true, nameField: 'subjectLabel'
  },
  { field: 'educationLevel1', kind: 'cv', i18n: 'education_level_1', cvType: CV_VALUE_EDUCATION, shortCode: true },
  { field: 'educationLevel2', kind: 'cv', i18n: 'education_level_2', cvType: CV_VALUE_COURSE_LEVEL, shortCode: true },
  { field: 'developmentManager', kind: 'team', i18n: 'development_manager' },
  { field: 'developmentProject', kind: 'text', i18n: 'development_project' },
  { field: 'layoutManager', kind: 'team', i18n: 'layout_manager' },
  { field: 'workType', kind: 'cv', i18n: 'work_type', cvType: CV_VALUE_PRODUCT_WORK_TYPE, onProduct: true },
  { field: 'assetsActive', kind: 'switch', i18n: 'assets_active' },
  { field: 'sendBtovenFromStructureLdx', kind: 'switch', i18n: 'send_btoven_from_structure_ldx' },
  { field: 'btovenCodeCourse', kind: 'text', i18n: 'btoven_code_course' },
  { field: 'btovenCodeSubject', kind: 'text', i18n: 'btoven_code_subject' },
  { field: 'collection', kind: 'cv', i18n: 'collection', cvType: CV_VALUE_PRODUCT_COLLECTION },
  { field: 'series', kind: 'cv', i18n: 'series', cvType: CV_VALUE_PRODUCT_SERIES },
  { field: 'author', kind: 'text', i18n: 'author' },
  { field: 'illustrator', kind: 'text', i18n: 'illustrator' },
  { field: 'readingAge', kind: 'text', i18n: 'reading_age' },
  { field: 'publicationDate', kind: 'date', i18n: 'publication_date' }
]

export const productFieldI18nKey = (definition: ProductFieldDefinition, suffix: 'label' | 'placeholder'): string =>
  `upsert_product_modal_component.${definition.i18n}_${suffix}`

/** Fields of a material type, titled with the type name */
export interface MaterialTypeGroup {
  codeId: string;
  name: string;
  fields: ProductFieldDefinition[];
}

const splitList = (value: string | null | undefined): string[] =>
  (value || '').split(',').map(item => item.trim()).filter(item => item)

const fieldName = (item: string): string =>
  item.endsWith(OPTIONAL_FIELD_SUFFIX) ? item.slice(0, -OPTIONAL_FIELD_SUFFIX.length).trim() : item

const typeFields = (types: CvValue[]): string[] =>
  types.reduce((all: string[], type) => all.concat(splitList(type.metadata)), [])

// Vocabulary order: the order number set in the controlled vocabulary section first, then as loaded (by name)
const orderOf = (type: CvValue): number =>
  type.orderNum === null || type.orderNum === undefined ? Number.MAX_SAFE_INTEGER : type.orderNum

const definitionOf = (field: string): ProductFieldDefinition | undefined =>
  PRODUCT_TYPE_FIELDS.find(definition => definition.field === field)

export class ProductFieldRules {
  private readonly materialTypes: CvValue[]
  private readonly projectTypes: string[]
  private readonly assignedFields: Set<string>
  private readonly projectFields: Set<string>
  private readonly projectRequiredFields: Set<string>

  constructor(materialTypes: CvValue[], project?: EditorialProject | null) {
    this.materialTypes = materialTypes
    this.projectTypes = splitList(project ? project.materialTypes : null)
    const projectItems = typeFields(materialTypes.filter(type => this.projectTypes.includes(type.codeId)))
    this.assignedFields = new Set(typeFields(materialTypes).map(fieldName))
    this.projectFields = new Set(projectItems.map(fieldName))
    this.projectRequiredFields = new Set(projectItems.filter(item => !item.endsWith(OPTIONAL_FIELD_SUFFIX)))
  }

  private isFixed(field: string): boolean {
    return PRODUCT_FIXED_REQUIRED_FIELDS.includes(field) || PRODUCT_FIXED_OPTIONAL_FIELDS.includes(field)
  }

  isVisible(field: string): boolean {
    return this.isFixed(field) || !this.assignedFields.has(field) || this.projectFields.has(field)
  }

  isRequired(field: string): boolean {
    if (PRODUCT_FIXED_REQUIRED_FIELDS.includes(field)) return true
    if (PRODUCT_BOOLEAN_FIELDS.includes(field) || this.isFixed(field)) return false
    return this.projectRequiredFields.has(field)
  }

  /** Fields of PRODUCT_TYPE_FIELDS that no material type lists */
  unassignedFields(): ProductFieldDefinition[] {
    return PRODUCT_TYPE_FIELDS.filter(definition => !this.assignedFields.has(definition.field))
  }

  /**
   * Fields of every material type, one group per type in the vocabulary order and the fields in
   * the order the type lists them. A field listed by several types only goes in one group: the
   * first of the project types, or else the first type. Types without fields are left out.
   */
  materialTypeGroups(): MaterialTypeGroup[] {
    const types = this.materialTypes.slice().sort((a, b) => orderOf(a) - orderOf(b))
    const isProjectType = (type: CvValue) => this.projectTypes.includes(type.codeId)
    const owners = new Map<string, string>()
    types.filter(isProjectType).concat(types.filter(type => !isProjectType(type))).forEach(type =>
      splitList(type.metadata).map(fieldName).forEach(field => {
        if (!owners.has(field)) owners.set(field, type.codeId)
      })
    )
    return types
      .map(type => ({
        codeId: type.codeId,
        name: type.name || type.codeId,
        fields: Array.from(new Set(splitList(type.metadata).map(fieldName)))
          .filter(field => owners.get(field) === type.codeId && !this.isFixed(field))
          .map(definitionOf)
          .filter((definition): definition is ProductFieldDefinition => !!definition)
      }))
      .filter(group => group.fields.length > 0)
  }
}

/**
 * Material types with their fields, read skipping the HTTP cache so a change made in the
 * controlled vocabulary section applies straight away
 */
export async function loadMaterialTypes(): Promise<CvValue[]> {
  const values = await ApiClient.getCvValuesByType(CV_VALUE_MATERIAL_TYPE, true)
  return values.filter(item => item.shortCode !== CV_VALUE_SHORT_CODE_INDEF)
}
